import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Globe, LogOut, Monitor, Moon, Search, Sun } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useVisibleNav } from '../lib/navigation';
import { setThemePref } from '../lib/theme';
import { LivenessDot } from './LivenessBadge';
import { extractDomain } from '../lib/domainInput';
import type { ApiResponse } from '../types';

interface DomainHit {
  id: string;
  domain_name: string;
  registrar_name?: string | null;
  liveness_status?: string | null;
  domain_type?: string | null;
}

type Item =
  | { kind: 'page'; key: string; label: string; hint: string; icon: typeof Globe; run: () => void }
  | { kind: 'domain'; key: string; hit: DomainHit; run: () => void }
  | { kind: 'action'; key: string; label: string; icon: typeof Globe; run: () => void };

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const PALETTE_SHORTCUT = isMac ? '⌘K' : 'Ctrl K';

/**
 * ⌘K / Ctrl+K command bar: jump to a page, open a domain by name, or run a
 * quick action. Mounted once in Layout; `open` is controlled there so the
 * sidebar's search button can open it too.
 */
export default function CommandPalette({ open, onClose, onLogout }: { open: boolean; onClose: () => void; onLogout: () => void }) {
  const navigate = useNavigate();
  const { fetchApi } = useApi();
  const groups = useVisibleNav();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [domains, setDomains] = useState<DomainHit[]>([]);
  const [searching, setSearching] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const reqId = useRef(0);

  useEffect(() => {
    if (open) { setQuery(''); setDomains([]); setActive(0); }
  }, [open]);

  // Debounced domain search; stale responses are dropped.
  useEffect(() => {
    const q = extractDomain(query).trim();
    if (!open || q.length < 2) { setDomains([]); setSearching(false); return; }
    const id = ++reqId.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetchApi<ApiResponse<DomainHit[]>>(`/domains?search=${encodeURIComponent(q)}&limit=8&sort_by=domain_name&sort_order=asc`);
        if (id === reqId.current) setDomains(res.data || []);
      } catch {
        if (id === reqId.current) setDomains([]);
      } finally {
        if (id === reqId.current) setSearching(false);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [query, open, fetchApi]);

  const go = (path: string) => { onClose(); navigate(path); };

  const items: Item[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pages: Item[] = groups.flatMap((g) => g.items
      .filter((i) => !q || i.name.toLowerCase().includes(q) || (i.keywords ?? '').includes(q) || (g.label ?? '').toLowerCase().includes(q))
      .map((i): Item => ({ kind: 'page', key: `p:${i.href}`, label: i.name, hint: g.label ?? '', icon: i.icon, run: () => go(i.href) })));
    const doms: Item[] = domains.map((d) => ({ kind: 'domain', key: `d:${d.id}`, hit: d, run: () => go(`/domains/${d.id}`) }));
    const actions: Item[] = ([
      { kind: 'action', key: 'a:light', label: 'Theme: Light', icon: Sun, run: () => { setThemePref('light'); onClose(); } },
      { kind: 'action', key: 'a:dark', label: 'Theme: Dark', icon: Moon, run: () => { setThemePref('dark'); onClose(); } },
      { kind: 'action', key: 'a:system', label: 'Theme: System', icon: Monitor, run: () => { setThemePref('system'); onClose(); } },
      { kind: 'action', key: 'a:logout', label: 'Sign out', icon: LogOut, run: () => { onClose(); onLogout(); } },
    ] as Item[]).filter((a) => a.kind === 'action' && (!q || a.label.toLowerCase().includes(q)));
    // Typing something that looks like a domain puts the domain hits first.
    return q.includes('.') || pages.length === 0 ? [...doms, ...pages, ...actions] : [...pages, ...doms, ...actions];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, groups, domains]);

  useEffect(() => { setActive(0); }, [query, domains]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); items[active]?.run(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let lastSection = '';
  const section = (it: Item) => (it.kind === 'page' ? 'Pages' : it.kind === 'domain' ? 'Domains' : 'Actions');

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]" role="presentation">
      <div className="absolute inset-0 bg-ink-950/60 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div role="dialog" aria-modal="true" aria-label="Command menu"
        className="relative w-full max-w-xl overflow-hidden rounded-xl bg-surface shadow-2xl ring-1 ring-slate-900/5 dark:ring-white/10 animate-[popIn_120ms_ease-out]">
        <div className="flex items-center gap-3 border-b border-slate-200 px-4">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData('text');
              const d = extractDomain(pasted);
              if (d !== pasted && d.includes('.')) { e.preventDefault(); setQuery(d); }
            }}
            onKeyDown={onKeyDown}
            placeholder="Search pages and domains…"
            className="h-12 flex-1 border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
          />
          {searching && <span className="text-xs text-slate-400">Searching…</span>}
          <kbd className="hidden sm:inline rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">Esc</kbd>
        </div>
        <div id="cmdk-list" ref={listRef} role="listbox" className="max-h-[50vh] overflow-y-auto py-2">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              {query.trim().length >= 2 && !searching ? `No pages or domains match “${query.trim()}”.` : 'Type to search.'}
            </div>
          )}
          {items.map((it, idx) => {
            const sec = section(it);
            const header = sec !== lastSection ? (lastSection = sec, sec) : null;
            const isActive = idx === active;
            const base = `mx-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-lg px-3 py-2 text-left text-sm ${isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700'}`;
            return (
              <div key={it.key}>
                {header && <div className="px-5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{header}</div>}
                <button type="button" role="option" aria-selected={isActive} data-idx={idx}
                  onMouseMove={() => setActive(idx)} onClick={() => it.run()} className={base}>
                  {it.kind === 'domain' ? (
                    <>
                      <Globe className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="flex-1 truncate font-medium">{it.hit.domain_name}</span>
                      <LivenessDot status={it.hit.liveness_status} />
                      <span className="max-w-[40%] truncate text-xs text-slate-400">{[it.hit.domain_type, it.hit.registrar_name].filter(Boolean).join(' · ')}</span>
                    </>
                  ) : (
                    <>
                      <it.icon className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="flex-1 truncate">{it.label}</span>
                      {it.kind === 'page' && it.hint && <span className="text-xs text-slate-400">{it.hint}</span>}
                    </>
                  )}
                  {isActive && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
